// src/pages/JobApplicants.js
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  apiFetch,
  post as apiPost,
} from "../services/api";

/* ----------------- Helpers ----------------- */
function decisionLabel(v) {
  const n = Number(v);
  if (n === 0) return "Not Considered";
  if (n === 1) return "In Progress";
  if (n === 2) return "Hired";
  if (n === 3) return "AI Recommended";
  return "-";
}
const PAGE_SIZES = [10, 20, 50];

function sanitizeHtml(html = "") {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    ["script", "style", "iframe", "object", "embed", "link", "meta"].forEach((t) =>
      doc.querySelectorAll(t).forEach((el) => el.remove())
    );
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
    while (walker.nextNode()) {
      const el = walker.currentNode;
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = (attr.value || "").trim();
        if (name.startsWith("on")) el.removeAttribute(attr.name);
        if (name === "style") el.removeAttribute(attr.name);
        if ((name === "href" || name === "src") && /^javascript:/i.test(value)) el.removeAttribute(attr.name);
        if (name === "target" && value === "_blank") el.setAttribute("rel", "noopener noreferrer");
      });
    }
    return doc.body.innerHTML;
  } catch {
    return "";
  }
}
function textOnly(html = "") {
  const el = document.createElement("div");
  el.innerHTML = sanitizeHtml(html);
  return el.textContent || el.innerText || "";
}

async function putJson(url, body, user) {
  const token =
    user?.token ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    "";
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(url, { method: "PUT", headers, body: JSON.stringify(body), credentials: "include" });
  const text = await res.text().catch(() => "");
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  try { return JSON.parse(text || "{}"); } catch { return {}; }
}

/* Matching score → badge classes per spec */
function scoreBadgeClasses(score) {
  const n = Number(score);
  if (!Number.isFinite(n) || n < 0) return ""; // handled separately
  if (n > 84) return "bg-green-600 text-white";         // >84 green
  if (n >= 70) return "bg-green-100 text-green-800";    // 70–84 light green
  if (n >= 60) return "bg-yellow-100 text-yellow-800";  // 60–69 light yellow
  if (n >= 50) return "bg-orange-100 text-orange-800";  // 50–59 light orange
  if (n >= 0)  return "bg-red-100 text-red-800";         // >=0 & <50 light red
  return "";
}

/* ----------------- Component ----------------- */
export default function JobApplicants() {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isSuperadmin = (user?.group_role || "").toLowerCase() === "superadmin";

  const initial_orgId = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const s = q.get("orgId");
    return s ? Number(s) : null;
  }, [location.search]);

  const [JOB_REQ, setJOB_REQ] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(PAGE_SIZES[0]);
  const [showFullDesc, setShowFullDesc] = useState(false);

  // candidates currently being evaluated -> show spinner
  const [evaluating, setEvaluating] = useState(() => new Set());

  /* --------- Load job header --------- */
  async function loadJobHeader() {
    const job = await apiFetch(`/api/jobs/${jobId}`);
    setJOB_REQ({
      JOB_REQ_job_req_id: job.JOB_REQ_job_req_id ?? Number(jobId),
      JOB_REQ_job_req_description: job.JOB_REQ_job_req_description ?? job.job_req_description ?? "",
      JOB_AREA_OF_INTEREST_job_area_of_interest_descr:
        job.JOB_AREA_OF_INTEREST_job_area_of_interest_descr ?? job.job_area_of_interest_descr ?? "-",
      JOB_LOCATION_job_location_descr:
        job.JOB_LOCATION_job_location_descr ?? job.job_location_descr ?? "-",
      COUNTRY_country_name: job.COUNTRY_country_name ?? job.country_name ?? "",
      JOB_REQ_organization_id: job.JOB_REQ_organization_id ?? job.organization_id ?? null,
    });
  }

  /* --------- Load submissions --------- */
  async function loadSubmissions() {
    let url = `/api/app_jobs/${jobId}/submissions`;
    if (isSuperadmin) {
      url = initial_orgId != null
        ? `/api/app_jobs/${jobId}/submissions?orgId=${initial_orgId}`
        : `/api/app_jobs/${jobId}/submissions?all=1`;
    }
    const baseList = await apiFetch(url);
    const list = Array.isArray(baseList) ? baseList : [];

    // Fill missing candidate info if backend didn't join (keeps your existing behavior)
    const needFetch = list.filter(
      (r) =>
        !r.CANDIDATE_candidate_name ||
        !r.CANDIDATE_candidate_email ||
        !r.CANDIDATE_candidate_phone
    );

    if (needFetch.length) {
      const cache = new Map();
      await Promise.all(
        needFetch.map(async (r) => {
          const cid =
            r.JOB_REQ_SUBMISSION_candidate_id ??
            r.JOB_REQ_SUBMISSION?.candidate_id ??
            r.CANDIDATE_candidate_id ??
            r.candidate_id;
          if (!cid || cache.has(cid)) return;
          try {
            const c = await apiFetch(`/api/candidates/${cid}`);
            if (c) cache.set(cid, c);
          } catch {}
        })
      );

      for (const r of list) {
        const cid =
          r.JOB_REQ_SUBMISSION_candidate_id ??
          r.JOB_REQ_SUBMISSION?.candidate_id ??
          r.CANDIDATE_candidate_id ??
          r.candidate_id;
        const c = cache.get(cid);
        if (c) {
          r.CANDIDATE_candidate_id = c.candidate_id ?? r.CANDIDATE_candidate_id ?? cid;
          r.CANDIDATE_candidate_name = c.candidate_name ?? r.CANDIDATE_candidate_name;
          r.CANDIDATE_candidate_email = c.candidate_email ?? r.CANDIDATE_candidate_email;
          r.CANDIDATE_candidate_phone = c.candidate_phone ?? r.CANDIDATE_candidate_phone;
          r.CANDIDATE_candidate_resume = c.candidate_resume ?? r.CANDIDATE_candidate_resume;
        }
      }
    }

    // Normalize % score if some endpoints send a top-level field
    for (const r of list) {
      if (
        r.JOB_REQ_SUBMISSION_candidate_matching_score == null &&
        (r.JOB_REQ_SUBMISSION?.candidate_matching_score ?? r.candidate_matching_score) != null
      ) {
        r.JOB_REQ_SUBMISSION_candidate_matching_score =
          r.JOB_REQ_SUBMISSION?.candidate_matching_score ?? r.candidate_matching_score;
      }
    }

    setRows(list);
  }

  async function load() {
    setLoading(true);
    try {
      await Promise.all([loadJobHeader(), loadSubmissions()]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (jobId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, initial_orgId]);

  /* --------- Header derivations --------- */
  const JOB_REQ_job_req_id = JOB_REQ?.JOB_REQ_job_req_id ?? Number(jobId);
  const desc = JOB_REQ?.JOB_REQ_job_req_description ?? "";
  const aoi = JOB_REQ?.JOB_AREA_OF_INTEREST_job_area_of_interest_descr ?? "-";
  const city = JOB_REQ?.JOB_LOCATION_job_location_descr ?? "-";
  const country = JOB_REQ?.COUNTRY_country_name ?? "";
  const locationText = [city, country].filter(Boolean).join(", ") || "-";

  const descHtml = sanitizeHtml(desc);
  const descPreview = textOnly(desc);
  const preview = descPreview.length > 260 ? `${descPreview.slice(0, 260)}…` : descPreview;

  /* --------- Paging --------- */
  const total = rows.length;
  const pageSizeToUse = pageSizeState || PAGE_SIZES[0];
  const totalPages = Math.max(1, Math.ceil(total / pageSizeToUse));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSizeToUse;
  const pageRows = rows.slice(startIdx, startIdx + pageSizeToUse);

  /* --------- Actions --------- */
  function resolveCandidateId(row) {
    return (
      row.JOB_REQ_SUBMISSION_candidate_id ??
      row.JOB_REQ_SUBMISSION?.candidate_id ??
      row.CANDIDATE_candidate_id ??
      row.candidate_id ??
      null
    );
  }

  function resumeHref(row) {
    const cid = resolveCandidateId(row);
    return cid ? `/api/jobs/${JOB_REQ_job_req_id}/candidates/${cid}/resume` : null;
  }

  function handleScheduleInterview(row) {
    const cid = resolveCandidateId(row);
    if (!cid || !JOB_REQ_job_req_id) return;

    const params = new URLSearchParams({
      create: "1",
      autoSelect: "1",
      jobId: String(JOB_REQ_job_req_id),
      candidateId: String(cid),
      candidate_id: String(cid),
      ...(JOB_REQ?.JOB_REQ_organization_id != null
        ? { orgId: String(JOB_REQ.JOB_REQ_organization_id) }
        : {}),
    }).toString();

    navigate(`/interviews?${params}`, { replace: false });
  }

  async function ruleOutApplicant(row) {
    // Confirm first (unchanged)
    if (!window.confirm("Are you sure you want to rule out the applicant from this job submission?")) return;

    const cid = resolveCandidateId(row);
    try {
      // NEW: server-side unified flow (set decision=0, send rejection email,
      // cancel any scheduled interviews, send cancel emails)
      await apiPost(`/api/app_jobs/${JOB_REQ_job_req_id}/candidates/${cid}/reject`, {});

      // Refresh list after server-side updates/emails
      await loadSubmissions();

      // Optional UX message
      alert("Applicant marked as Not Considered. Any scheduled interviews were canceled and emails have been sent.");
    } catch (e) {
      console.error("Reject applicant failed:", e);
      // Fallback to just refreshing
      await loadSubmissions();
      alert("Failed to complete the rejection workflow.");
    }
  }

  // -------- Evaluate: call backend -> backend calls external -> updates DB --------
  async function evaluateCandidate(row) {
    const cid = resolveCandidateId(row);
    if (!cid || !JOB_REQ_job_req_id) return;
    // Mark evaluating
    setEvaluating((prev) => {
      const next = new Set(prev);
      next.add(cid);
      return next;
    });
    try {
      // Prefer app_jobs route; backend updates DB + returns score
      const res = await putJson(
        `/api/app_jobs/${JOB_REQ_job_req_id}/candidates/${cid}/evaluate`,
        {},
        user
      );

      const newScore = Number(
        res?.candidate_matching_score ??
        res?.JOB_REQ_SUBMISSION_candidate_matching_score ??
        res?.score ??
        0
      );

      // Update the specific row in-place for snappy UI; then refresh from server
      setRows((old) =>
        old.map((r) => {
          const rid = resolveCandidateId(r);
          if (rid !== cid) return r;
          const updated = { ...r };
          updated.JOB_REQ_SUBMISSION_candidate_matching_score = newScore;
          if (updated.JOB_REQ_SUBMISSION) {
            updated.JOB_REQ_SUBMISSION = {
              ...updated.JOB_REQ_SUBMISSION,
              candidate_matching_score: newScore,
            };
          }
          updated.candidate_matching_score = newScore;
          return updated;
        })
      );

      // Re-sync list (in case backend sets other fields)
      await loadSubmissions();
    } catch (e) {
      console.error("Evaluate error", e);
    } finally {
      setEvaluating((prev) => {
        const next = new Set(prev);
        next.delete(cid);
        return next;
      });
    }
  }

  /* --------- Render --------- */
  return (
    <div>
      <Navbar />
      <div className="p-8">
        {/* Header */}
        <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 mb-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              <div>
                <div className="text-sm text-gray-600">Job ID</div>
                <div className="font-semibold">{JOB_REQ_job_req_id}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Area of Interest</div>
                <div className="font-semibold">{aoi}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Location</div>
                <div className="font-semibold">{locationText}</div>
              </div>
            </div>
            <div className="ml-auto">
              <Link className="text-blue-700 underline" to="/jobs">← Back to Jobs</Link>
            </div>
          </div>

          <div className="mt-3 text-sm text-gray-800">
            <span className="font-medium mr-2">Job Description:</span>
            {!showFullDesc ? (
              <>
                <span>{preview || "-"}</span>{" "}
                {descPreview && descPreview.length > 260 && (
                  <button type="button" className="text-blue-700 underline" onClick={() => setShowFullDesc(true)}>
                    More…
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="prose max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2" dangerouslySetInnerHTML={{ __html: descHtml }} />
                <button type="button" className="text-blue-700 underline mt-1 inline-block" onClick={() => setShowFullDesc(false)}>
                  Show less
                </button>
              </>
            )}
          </div>
        </div>

        {/* Applicants */}
        {loading ? (
          <div>Loading…</div>
        ) : (
          <>
            <table className="w-full bg-white rounded shadow text-left">
              <thead>
                <tr>
                  <th className="p-2 border-b">Candidate ID</th>
                  <th className="p-2 border-b">Name</th>
                  <th className="p-2 border-b">Email</th>
                  <th className="p-2 border-b">Phone</th>
                  <th className="p-2 border-b">Resume</th>
                  <th className="p-2 border-b">Submitted At</th>

                  {/* % (before X) */}
                  <th className="p-2 border-b text-center" title="Matching Score">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-gray-800">%</span>
                  </th>

                  {/* X */}
                  <th className="p-2 border-b text-center" title="Do NOT Consider Applicant">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 inline-block" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 8.586L5.293 3.879 3.879 5.293 8.586 10l-4.707 4.707 1.414 1.414L10 11.414l4.707 4.707 1.414-1.414L11.414 10l4.707-4.707-1.414-1.414L10 8.586z" clipRule="evenodd" />
                    </svg>
                  </th>

                  <th className="p-2 border-b">Decision</th>
                  <th className="p-2 border-b text-right">Schedule</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(startIdx, startIdx + pageSizeToUse).map((row, idx) => {
                  const candidateId =
                    row.JOB_REQ_SUBMISSION_candidate_id ??
                    row.JOB_REQ_SUBMISSION?.candidate_id ??
                    row.CANDIDATE_candidate_id ??
                    row.candidate_id ??
                    "-";

                  const submissionDt =
                    row.JOB_REQ_SUBMISSION_submission_datetime ??
                    row.JOB_REQ_SUBMISSION?.submission_datetime ??
                    row.submission_datetime ??
                    null;

                  const matchScoreRaw =
                    row.JOB_REQ_SUBMISSION_candidate_matching_score ??
                    row.JOB_REQ_SUBMISSION?.candidate_matching_score ??
                    row.candidate_matching_score ??
                    null;
                  const matchScore = Number.isFinite(Number(matchScoreRaw))
                    ? Number(matchScoreRaw)
                    : 0;

                  const jobDecision =
                    row.JOB_REQ_SUBMISSION_job_decision ??
                    row.JOB_REQ_SUBMISSION?.job_decision ??
                    row.job_decision ??
                    1;
                  const canSchedule = Number(jobDecision) === 1;

                  const name = row.CANDIDATE_candidate_name ?? "-";
                  const email = row.CANDIDATE_candidate_email ?? "-";
                  const phone = row.CANDIDATE_candidate_phone ?? "-";

                  const resumeUrl = (() => {
                    const cid =
                      row.JOB_REQ_SUBMISSION_candidate_id ??
                      row.JOB_REQ_SUBMISSION?.candidate_id ??
                      row.CANDIDATE_candidate_id ??
                      row.candidate_id ??
                      null;
                    return cid ? `/api/jobs/${JOB_REQ_job_req_id}/candidates/${cid}/resume` : null;
                  })();

                  const submittedAt = submissionDt
                    ? new Date(submissionDt).toLocaleString()
                    : "-";

                  const cidForEval = Number(
                    row.JOB_REQ_SUBMISSION_candidate_id ??
                    row.JOB_REQ_SUBMISSION?.candidate_id ??
                    row.CANDIDATE_candidate_id ??
                    row.candidate_id ??
                    NaN
                  );
                  const isEvaluating = evaluating.has(cidForEval);

                  return (
                    <tr key={`${candidateId}-${idx}`} className="hover:bg-gray-50">
                      <td className="p-2 border-b">{candidateId}</td>
                      <td className="p-2 border-b">{name}</td>
                      <td className="p-2 border-b">{email}</td>
                      <td className="p-2 border-b">{phone}</td>

                      {/* Resume (icon only) */}
                      <td className="p-2 border-b">
                        {resumeUrl ? (
                          <a
                            href={resumeUrl}
                            download
                            title="Download resume"
                            className="inline-flex items-center p-1 rounded hover:bg-blue-50 text-blue-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                              <path d="M12 3a1 1 0 0 1 1 1v8.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4.001 4a1 1 0 0 1-1.412 0l-4.001-4a1 1 0 1 1 1.414-1.414L11 12.586V4a1 1 0 0 1 1-1z" />
                              <path d="M5 20a2 2 0 0 1-2-2v-2a1 1 0 1 1 2 0v2h14v-2a1 1 0 1 1 2 0v2a2 2 0 0 1-2 2H5z" />
                            </svg>
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>

                      {/* Submitted At */}
                      <td className="p-2 border-b">{submittedAt}</td>

                      {/* % */}
                      <td className="p-2 border-b text-center">
                        {matchScore == null ? (
                          <span className="text-gray-400">—</span>
                        ) : matchScore < 0 ? (
                          isEvaluating ? (
                            <span className="inline-flex items-center justify-center p-1" title="Evaluating…">
                              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                              </svg>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="inline-flex items-center p-1 rounded hover:bg-indigo-50 text-indigo-700"
                              title="Evaluate (compute matching score)"
                              onClick={(e) => {
                                e.stopPropagation();
                                evaluateCandidate(row);
                              }}
                            >
                              {/* Sparkles icon */}
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path d="M8.255 3.156a.75.75 0 011.49 0l.162 1.75a4.5 4.5 0 003.887 3.887l1.75.162a.75.75 0 010 1.49l-1.75.162a4.5 4.5 0 00-3.887 3.887l-.162 1.75a.75.75 0 01-1.49 0l-.162-1.75A4.5 4.5 0 003.206 10.6l-1.75-.162a.75.75 0 010-1.49l1.75-.162a4.5 4.5 0 003.887-3.887l.162-1.75z" />
                              </svg>
                            </button>
                          )
                        ) : (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${scoreBadgeClasses(matchScore)}`}
                            title={`Matching Score: ${matchScore}%`}
                          >
                            {matchScore}%
                          </span>
                        )}
                      </td>

                      {/* X */}
                      <td className="p-2 border-b text-center">
                        {canSchedule ? (
                          <button
                            type="button"
                            className="inline-flex items-center p-1 rounded hover:bg-red-50 text-red-700"
                            title="Do NOT Consider Applicant"
                            onClick={(e) => {
                              e.stopPropagation();
                              ruleOutApplicant(row);
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 8.586L5.293 3.879 3.879 5.293 8.586 10l-4.707 4.707 1.414 1.414L10 11.414l4.707 4.707 1.414-1.414L11.414 10l4.707-4.707-1.414-1.414L10 8.586z" clipRule="evenodd" />
                            </svg>
                          </button>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>

                      {/* Decision */}
                      <td className="p-2 border-b">{decisionLabel(jobDecision)}</td>

                      {/* Schedule */}
                      <td className="p-2 border-b text-right">
                        <button
                          type="button"
                          disabled={!canSchedule}
                          onClick={() => handleScheduleInterview(row)}
                          className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
                          title={canSchedule ? "Schedule interview" : "Enable when status is In Progress"}
                        >
                          Schedule Interview
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-3 text-gray-500">No applicants found for this job.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-3">
              <div className="text-sm text-gray-600">
                Showing <b>{total === 0 ? 0 : startIdx + 1}</b>–<b>{Math.min(startIdx + pageSizeToUse, total)}</b> of <b>{total}</b>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 border rounded disabled:opacity-50" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                <span className="text-sm">Page {currentPage} / {totalPages}</span>
                <button className="px-3 py-1 border rounded disabled:opacity-50" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
                <select className="ml-3 border p-1 rounded text-sm" value={pageSizeToUse} onChange={(e) => { setPageSizeState(Number(e.target.value)); setPage(1); }}>
                  {PAGE_SIZES.map((s) => (<option key={s} value={s}>{s} / page</option>))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
